/**
 * Parse a date string ensuring UTC interpretation.
 * Prisma returns ISO 8601 strings (e.g., "2024-01-15T12:00:00.000Z").
 * If the 'Z' suffix is missing (possible with some DB/serialization configs),
 * the string would be parsed as local time, causing timezone offset bugs.
 */
function ensureUTC(date: Date | string): Date {
  if (date instanceof Date) return date;
  // If the string has no timezone indicator, append 'Z' to force UTC
  if (!date.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(date)) {
    return new Date(date + 'Z');
  }
  return new Date(date);
}

export function formatDistanceToNow(date: Date | string): string {
  const parsed = ensureUTC(date);
  const now = new Date();
  const diffMs = now.getTime() - parsed.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) {
    return 'только что';
  }

  if (diffMins < 60) {
    return `${diffMins} мин. назад`;
  }

  if (diffHours < 24) {
    return `${diffHours} ч. назад`;
  }

  if (diffDays === 1) {
    return 'вчера';
  }

  if (diffDays < 7) {
    return `${diffDays} дн. назад`;
  }

  // Format as date
  return parsed.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
  });
}

export function formatDate(date: Date): string {
  return date.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function formatTime(date: Date): string {
  return date.toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function getDaysDifference(date1: Date, date2: Date): number {
  const diffMs = date1.getTime() - date2.getTime();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

/** Склонение: 1 день, 2 дня, 5 дней */
export function pluralizeDays(n: number): string {
  const abs = Math.abs(n);
  const mod10 = abs % 10;
  const mod100 = abs % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n} день`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} дня`;
  return `${n} дней`;
}

/** «Каждый день», «Каждые 2 дня», «Каждые 5 дней» */
export function everyNDays(n: number): string {
  if (n === 1) return 'Каждый день';
  const abs = Math.abs(n);
  const mod10 = abs % 10;
  const mod100 = abs % 100;
  if (mod10 === 1 && mod100 !== 11) return `Каждый ${n} день`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `Каждые ${n} дня`;
  return `Каждые ${n} дней`;
}
